// ════════════════════════════════════════════════════════════════
// MASTER APP CONFIGURATION — The heart of the Scalable Factory
// ════════════════════════════════════════════════════════════════
//
// This file defines the universal configuration interface that drives
// the entire MasterShell layout. To deploy a new niche app, you create
// a new config object and import it in main.tsx. No other code changes
// are required.
//
// The schema (db/database.ts) is generic and reads entity field
// definitions from this config to render forms dynamically.
// ════════════════════════════════════════════════════════════════

// ── Field Definitions (drives dynamic form rendering) ──

export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'textarea'
  | 'select'
  | 'encrypted'
  | 'boolean';

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  encrypted?: boolean; // If true, value is encrypted before storage
  halfWidth?: boolean; // For two-column form layouts
}

// ── Entity Definitions (maps schema tables to UI labels) ──

export interface EntityDefinition {
  singular: string;
  plural: string;
  icon: string; // SF Symbol-style name or emoji
  fields: FieldDefinition[];
}

// ── Widget Definitions (drives the navigation and content area) ──

export type WidgetType = 'list' | 'summary' | 'history' | 'settings' | 'canvas';

export interface WidgetDefinition {
  id: string;
  label: string;
  icon: string;
  type: WidgetType;
  entityId?: 'collection' | 'item' | 'identifier' | 'ledgerEntry';
}

// ── Monetization Configuration ──

export interface MonetizationConfig {
  adSlotEnabled: boolean;
  adSlotId: string; // Google AdMob slot ID or AdSense client ID
  adSlotPlaceholder: string; // Text shown when no ad is loaded
  proPrice: number;
  proFeatures: string[];
  paymentLink: string; // Stripe/Gumroad payment URL
  licenseWorkerUrl?: string; // Cloudflare Worker for license validation
}

// ── Canvas Configuration (game architecture compatibility) ──

export interface CanvasConfig {
  enabled: boolean;
  mode: 'chart' | 'game' | 'custom';
  width: number;
  height: number;
  // When mode === 'game', this would point to the game engine init function
  gameModuleUrl?: string;
}

// ── Security Configuration ──

export interface SecurityConfig {
  masterPassphraseRequired: boolean;
  autoLockMinutes: number;
  seedPhraseGuard: boolean;
}

// ── App Configuration (the master interface) ──

export interface AppConfig {
  app: {
    name: string;
    shortName: string;
    description: string;
    themeColor: string;
    backgroundColor: string;
    accentColor: string;
  };
  entities: {
    collection: EntityDefinition;
    item: EntityDefinition;
    identifier: EntityDefinition;
    ledgerEntry: EntityDefinition;
  };
  widgets: WidgetDefinition[];
  monetization: MonetizationConfig;
  canvas: CanvasConfig;
  security: SecurityConfig;
}

// ════════════════════════════════════════════════════════════════
// COLD WALLET LEDGER CONFIGURATION
// The first niche deployment using the Scalable Factory pattern.
// ════════════════════════════════════════════════════════════════

export const coldWalletConfig: AppConfig = {
  app: {
    name: 'Cold Vault Ledger',
    shortName: 'VaultLedger',
    description: 'Offline hardware wallet inventory & asset tracker',
    themeColor: '#0f172a',
    backgroundColor: '#0f172a',
    accentColor: '#3b82f6',
  },

  entities: {
    collection: {
      singular: 'Wallet',
      plural: 'Wallets',
      icon: 'creditcard',
      fields: [
        { key: 'name', label: 'Wallet Name', type: 'text', required: true, placeholder: 'Ledger Nano X #1' },
        { key: 'category', label: 'Model', type: 'text', required: true, placeholder: 'Ledger Nano X' },
        { key: 'manufacturer', label: 'Manufacturer', type: 'text', placeholder: 'Ledger', halfWidth: true },
        { key: 'firmwareVersion', label: 'Firmware', type: 'text', placeholder: 'v2.2.3', halfWidth: true },
        { key: 'serialNumberEncrypted', label: 'Serial Number', type: 'encrypted', placeholder: 'Encrypted on save' },
        { key: 'purchaseDate', label: 'Purchase Date', type: 'date', halfWidth: true },
        { key: 'purchasePrice', label: 'Purchase Price', type: 'number', placeholder: '0.00', halfWidth: true },
        { key: 'notes', label: 'Notes', type: 'textarea' },
      ],
    },

    item: {
      singular: 'Asset',
      plural: 'Assets',
      icon: 'bitcoinsign',
      fields: [
        { key: 'name', label: 'Asset Name', type: 'text', required: true, placeholder: 'Bitcoin' },
        { key: 'symbol', label: 'Symbol', type: 'text', required: true, placeholder: 'BTC', halfWidth: true },
        { key: 'balance', label: 'Balance', type: 'number', required: true, placeholder: '0.00000000', halfWidth: true },
        { key: 'purchaseDate', label: 'Purchase Date', type: 'date', halfWidth: true },
        { key: 'purchasePricePerUnit', label: 'Price / Unit', type: 'number', placeholder: '0.00', halfWidth: true },
        { key: 'notes', label: 'Notes', type: 'textarea' },
      ],
    },

    identifier: {
      singular: 'Address',
      plural: 'Addresses',
      icon: 'qrcode',
      fields: [
        { key: 'valueEncrypted', label: 'Public Address', type: 'encrypted', required: true, placeholder: 'bc1q...' },
        { key: 'label', label: 'Label', type: 'text', required: true, placeholder: 'Main deposit address' },
        { key: 'isPrimary', label: 'Primary', type: 'boolean' },
        { key: 'addressType', label: 'Address Type', type: 'select', options: ['segwit', 'legacy', 'taproot'] },
        { key: 'notes', label: 'Notes', type: 'textarea' },
      ],
    },

    ledgerEntry: {
      singular: 'Transaction',
      plural: 'Transactions',
      icon: 'arrow.left.arrow.right',
      fields: [
        { key: 'entryType', label: 'Type', type: 'select', required: true, options: ['purchase', 'transfer_in', 'transfer_out', 'snapshot'] },
        { key: 'amount', label: 'Amount', type: 'number', required: true, placeholder: '0.00000000', halfWidth: true },
        { key: 'pricePerUnit', label: 'Price / Unit', type: 'number', placeholder: '0.00', halfWidth: true },
        { key: 'totalValue', label: 'Total Value', type: 'number', placeholder: '0.00', halfWidth: true },
        { key: 'source', label: 'Source', type: 'text', placeholder: 'Coinbase, Kraken', halfWidth: true },
        { key: 'date', label: 'Date', type: 'date', required: true },
        { key: 'notes', label: 'Notes', type: 'textarea' },
      ],
    },
  },

  widgets: [
    { id: 'wallets', label: 'Wallets', icon: 'creditcard', type: 'list', entityId: 'collection' },
    { id: 'summary', label: 'Summary', icon: 'chart.pie', type: 'summary' },
    { id: 'history', label: 'History', icon: 'clock', type: 'history' },
    { id: 'settings', label: 'Settings', icon: 'gearshape', type: 'settings' },
  ],

  monetization: {
    adSlotEnabled: true,
    adSlotId: 'ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX',
    adSlotPlaceholder: 'Ad Space — Upgrade to Pro to remove ads',
    proPrice: 7.99,
    proFeatures: [
      'Unlimited wallets, assets, and addresses',
      'Encrypted backup export & import',
      'Advanced charts & cost basis tracking',
      'Custom tags & fields',
      'Seed phrase detection guard',
      'Auto-lock timer customization',
      'Remove all advertisements',
    ],
    paymentLink: 'https://stripe.com',
    licenseWorkerUrl: '', // Emptied locally to bypass local server XML access errors
  },

  canvas: {
    enabled: true,
    mode: 'chart',
    width: 800,
    height: 400,
  },

  security: {
    masterPassphraseRequired: true,
    autoLockMinutes: 5,
    seedPhraseGuard: true,
  },
};
